import { type ZodType, z } from "zod/v4";
import type { AuthData } from "../../types";
import { ClientFacingError } from "../common/errors";
import { measureFunctionTime } from "../common/utils";
import type { EncryptionService } from "../encryption";
import type { FrameLogger } from "../logger";
import type { Environment } from "./environment";

export class CrossmintHttpError extends ClientFacingError {
	constructor(
		public readonly status: number,
		public readonly statusText: string,
		public readonly url: string,
		public readonly responseBody?: unknown,
	) {
		const clientMessage = `HTTP ${status}: ${statusText}`;
		super(clientMessage);
		this.name = "CrossmintHttpError";
	}
}

export interface CrossmintRequestOptions<I, O> {
	name?: string;
	inputSchema: ZodType<I>;
	outputSchema: ZodType<O>;
	environment: Environment;
	authData?: AuthData;
	endpoint: (input: I) => string;
	method: "GET" | "POST" | "PUT" | "DELETE";
	encrypted?: boolean;
	/**
	 * Input fields sent next to the ciphertext instead of inside it, for the relay to read.
	 * Only meaningful when `encrypted` is set.
	 */
	unencryptedFields?: readonly (keyof I & string)[];
	encryptionService: EncryptionService;
	getHeaders: (authData?: AuthData) => Record<string, string>;
	fetchImpl?: typeof fetch;
	logger: FrameLogger;
}

const base64StringSchema = z
	.string()
	.regex(/^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/);

export class CrossmintRequest<
	method extends "GET" | "POST" | "PUT" | "DELETE",
	I extends method extends "GET" | "DELETE"
		? undefined
		: Record<string, unknown>,
	O extends Record<string, unknown>,
> {
	private environment: Environment;
	private basePath = "api/ncs/v1";
	private authData: AuthData | undefined;
	private inputSchema: ZodType<I>;
	private outputSchema: ZodType<O>;
	private method: string;
	private encrypted: boolean;
	private unencryptedFields: readonly (keyof I & string)[];
	private encryptionService: EncryptionService;
	private encryptedPayloadSchema = z.object({
		ciphertext: base64StringSchema,
		encapsulatedKey: base64StringSchema,
		publicKey: base64StringSchema,
	});
	private logPrefix: string;
	private logger: FrameLogger;

	private endpoint: (input: I) => string;
	private getHeaders: (authData?: AuthData) => Record<string, string>;
	private fetchImpl: typeof fetch;

	constructor(options: CrossmintRequestOptions<I, O>) {
		this.inputSchema = options.inputSchema;
		this.outputSchema = options.outputSchema;
		this.method = options.method;
		this.encrypted = options.encrypted || false;
		this.unencryptedFields = options.unencryptedFields ?? [];
		this.encryptionService = options.encryptionService;
		this.authData = options.authData;
		this.environment = options.environment;
		this.endpoint = options.endpoint;
		this.getHeaders = options.getHeaders;
		this.fetchImpl = options.fetchImpl || fetch.bind(window);
		this.logPrefix = `[Request${options.name ? `: ${options.name}` : ""}]`;
		this.logger = options.logger;
	}

	async execute(input: I): Promise<O> {
		this.logger.debug(
			this.logPrefix,
			`Executing ${this.encrypted ? "encrypted" : "unencrypted"} ${this.method} request...`,
		);
		this.logger.debug(
			this.logPrefix,
			`[TRACE] Parsing input ${JSON.stringify(input, null, 2)}...`,
		);
		const parsedInput = this.inputSchema.parse(input);
		const bodyObject = parsedInput
			? await this.constructBody(parsedInput)
			: undefined;
		this.logger.debug(
			this.logPrefix,
			`[TRACE] Body: ${JSON.stringify(bodyObject, null, 2)}...`,
		);
		const body = bodyObject ? JSON.stringify(bodyObject) : undefined;
		const headers = this.getHeaders(this.authData);
		const url = new URL(
			this.basePath + this.endpoint(parsedInput),
			this.getUrlFromEnvironment(this.environment),
		);

		const { result: response, time } = await measureFunctionTime(async () =>
			this.fetchImpl(url.toString(), {
				method: this.method,
				body,
				headers,
			}),
		);
		this.logger.debug(
			this.logPrefix,
			`${this.method} request to ${url.toString()} completed in ${time}ms`,
		);

		// Check if the response is successful (2xx status codes)
		if (!response.ok) {
			let responseBody: unknown;
			try {
				responseBody = await response.json();
			} catch {
				// If response body is not JSON, ignore it
				responseBody = undefined;
			}

			throw new CrossmintHttpError(
				response.status,
				response.statusText,
				url.toString(),
				responseBody,
			);
		}

		const json = await response.json();
		return this.outputSchema.parse(json);
	}

	private async constructBody(
		parsedInput: I,
	): Promise<
		I | (z.infer<typeof this.encryptedPayloadSchema> & Record<string, unknown>)
	> {
		if (parsedInput == null) {
			throw new Error("Error parsing body: input is null");
		}
		if (this.encrypted) {
			this.logger.debug(
				this.logPrefix,
				"Encrypting request. Encrypting body...",
			);
			if (!this.encryptionService)
				throw new Error("EncryptionService not provided");
			const { plaintext, toEncrypt } = this.splitUnencryptedFields(parsedInput);
			const encryptedPayload = this.encryptedPayloadSchema.parse(
				await this.encryptionService.encrypt(toEncrypt),
			);
			this.logger.debug(
				this.logPrefix,
				"Encryption successful! Encrypted body: ",
				JSON.stringify(encryptedPayload, null, 2),
			);
			return { ...plaintext, ...encryptedPayload };
		}
		return parsedInput;
	}

	private splitUnencryptedFields(parsedInput: NonNullable<I>): {
		plaintext: Record<string, unknown>;
		toEncrypt: Record<string, unknown>;
	} {
		const plaintext: Record<string, unknown> = {};
		const toEncrypt: Record<string, unknown> = { ...parsedInput };
		for (const field of this.unencryptedFields) {
			if (toEncrypt[field] != null) {
				plaintext[field] = toEncrypt[field];
			}
			delete toEncrypt[field];
		}
		return { plaintext, toEncrypt };
	}

	private getUrlFromEnvironment(environment: Environment) {
		switch (environment) {
			case "development":
				return "http://localhost:3000/";
			case "staging":
				return "https://staging.crossmint.com/";
			case "loadtest":
				return "https://loadtest.crossmint.com/";
			case "production":
				return "https://www.crossmint.com/";
			default:
				throw new Error("Invalid environment");
		}
	}
}
